<section class="ezri-panel" role="dialog" aria-modal="false" aria-labelledby="ezri-title"
         (keydown.escape)="close()">
  <header class="ezri-header">
    <span class="ezri-avatar" [class.ezri-avatar-busy]="store.sending()" aria-hidden="true"><svg class="ezri-stars" viewBox="0 0 24 24" aria-hidden="true"><path class="ezri-mark-star ezri-mark-star-big" d="M11.5 9.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5z"/><path class="ezri-mark-star ezri-mark-star-top" d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9z"/><path class="ezri-mark-star ezri-mark-star-bottom" d="M19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"/></svg></span>
    <h2 id="ezri-title" class="ezri-title">{{ 'assistant.title' | translate }}</h2>
    @if (!store.isEmpty()) {
      <button type="button" class="ezri-icon-btn" (click)="newConversation()"
              [attr.aria-label]="'assistant.newConversation' | translate"
              [attr.title]="'assistant.newConversation' | translate">
        <mat-icon>add_comment</mat-icon>
      </button>
    }
    <button type="button" class="ezri-icon-btn ezri-close" (click)="close()"
            [attr.aria-label]="'assistant.close' | translate"
            [attr.title]="'assistant.close' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  @if (feedback() !== 'off') {
    <!-- Asked once when leaving a real conversation; skipping is always one click. -->
    <div class="ezri-feedback" role="group" aria-labelledby="ezri-feedback-title">
      @if (feedback() === 'thanks') {
        <div class="ezri-feedback-thanks" role="status">
          <mat-icon aria-hidden="true">favorite</mat-icon>
          <p id="ezri-feedback-title">{{ 'assistant.feedback.thanks' | translate }}</p>
        </div>
      } @else {
        <h3 id="ezri-feedback-title" class="ezri-feedback-title">{{ 'assistant.feedback.title' | translate }}</h3>
        <p class="ezri-feedback-subtitle">{{ 'assistant.feedback.subtitle' | translate }}</p>
        <div class="ezri-stars" role="radiogroup" [attr.aria-label]="'assistant.feedback.ratingLabel' | translate">
          @for (n of stars; track n) {
            <button type="button" class="ezri-star" role="radio"
                    [class.ezri-star-on]="n <= rating()"
                    [attr.aria-checked]="n === rating()"
                    [attr.aria-label]="'assistant.feedback.star' | translate: { n: n }"
                    [disabled]="feedback() === 'sending'"
                    (click)="setRating(n)">
              <mat-icon aria-hidden="true">{{ n <= rating() ? 'star' : 'star_border' }}</mat-icon>
            </button>
          }
        </div>
        <label class="ezri-feedback-label" for="ezri-feedback-comment">{{ 'assistant.feedback.commentLabel' | translate }}</label>
        <textarea id="ezri-feedback-comment" class="ezri-input ezri-feedback-comment" rows="3" dir="auto"
                  [attr.maxlength]="feedbackMaxLength"
                  [placeholder]="'assistant.feedback.commentPlaceholder' | translate"
                  [ngModel]="feedbackComment()" (ngModelChange)="feedbackComment.set($event)"
                  [disabled]="feedback() === 'sending'"></textarea>
        <div class="ezri-feedback-actions">
          <button type="button" class="ezri-btn ezri-btn-primary ezri-feedback-send"
                  [disabled]="rating() < 1 || feedback() === 'sending'" (click)="sendFeedback()">
            {{ 'assistant.feedback.send' | translate }}
          </button>
          <button type="button" class="ezri-btn ezri-btn-secondary ezri-feedback-skip"
                  [disabled]="feedback() === 'sending'" (click)="skipFeedback()">
            {{ 'assistant.feedback.skip' | translate }}
          </button>
        </div>
      }
    </div>
  } @else {
  <div class="ezri-messages" #scroller role="log" aria-live="polite" [attr.aria-busy]="store.sending()">
    @if (store.isEmpty()) {
      <!-- Ezri introduces itself on every empty conversation: fixed text, so no model call. -->
      <div class="ezri-row ezri-row-bot">
        <div class="ezri-bubble ezri-bubble-bot ezri-intro" dir="auto">
          <p class="ezri-intro-hello">{{ 'assistant.intro.hello' | translate }}</p>
          <ul class="ezri-intro-list">
            @for (key of introKeys; track key) {
              <li>{{ key | translate }}</li>
            }
          </ul>
          <p class="ezri-intro-note">{{ 'assistant.intro.note' | translate }}</p>
        </div>
      </div>
      <div class="ezri-suggestions">
        @for (key of suggestionKeys(); track key) {
          <button type="button" class="ezri-chip" dir="auto" (click)="useSuggestion(key)"
                  [disabled]="store.sending()">
            {{ key | translate }}
          </button>
        }
      </div>
    }

    @for (entry of store.entries(); track entry.id) {
      @if (entry.kind === 'user') {
        <div class="ezri-row ezri-row-user">
          @if (entry.attachment; as file) {
            <!-- The picture is in memory only; after a reload the chip remains. -->
            <div class="ezri-receipt" [class.ezri-receipt-scanning]="isScanning(entry.id)">
              @if (thumbnail(entry.id); as src) {
                <div class="ezri-receipt-thumb">
                  <img [src]="src" alt="" (error)="$any($event.target).hidden = true">
                  @if (isScanning(entry.id)) { <span class="ezri-scan-line" aria-hidden="true"></span> }
                </div>
              } @else if (isScanning(entry.id)) {
                <div class="ezri-receipt-thumb ezri-receipt-thumb-file" aria-hidden="true">
                  <mat-icon>{{ file.kind === 'pdf' ? 'picture_as_pdf' : 'image' }}</mat-icon>
                  <span class="ezri-scan-line"></span>
                </div>
              }
              <div class="ezri-file-chip" dir="auto">
                <mat-icon aria-hidden="true">{{ file.kind === 'pdf' ? 'picture_as_pdf' : 'receipt_long' }}</mat-icon>
                <span class="ezri-file-name">{{ file.name }}</span>
                <span class="ezri-file-kind">{{ (file.kind === 'pdf' ? 'assistant.receipt.kindPdf' : 'assistant.receipt.kindImage') | translate }}</span>
              </div>
              @if (isScanning(entry.id)) {
                <div class="ezri-receipt-stage" role="status">{{ 'assistant.receipt.reading' | translate }}</div>
              }
            </div>
          }
          <div class="ezri-bubble ezri-bubble-user" dir="auto">{{ entry.text }}</div>
        </div>
      } @else if (entry.kind === 'assistant') {
        <div class="ezri-row ezri-row-bot">
          @if (entry.text) {
            <div class="ezri-bubble ezri-bubble-bot" dir="auto">@for (part of entry.text | ezriTextParts; track $index) {@if (part.href) {<a class="ezri-link" [href]="part.href" target="_blank" rel="noopener noreferrer">{{ part.text }}</a>} @else {<span>{{ part.text }}</span>}}</div>
          }
          @if (entry.card; as card) {
            <div class="ezri-card" [class.ezri-card-resolved]="card.state === 'resolved'"
                 [class.ezri-card-busy]="card.state === 'busy'"
                 [class.ezri-card-done]="card.state === 'resolved' && isDone(card.resultCode)"
                 role="group" [attr.aria-labelledby]="'ezri-card-title-' + entry.id">
              <div class="ezri-card-title" [id]="'ezri-card-title-' + entry.id">{{ actionTitle(card.action.tool) }}</div>
              <dl class="ezri-fields">
                @for (field of card.action.fields; track $index) {
                  <div class="ezri-field">
                    <dt>{{ fieldLabel(field.key) }}</dt>
                    <dd dir="auto">{{ fieldValue(field.value, field.key) }}</dd>
                  </div>
                }
              </dl>
              @if (card.state !== 'resolved') {
                <div class="ezri-card-actions">
                  <button type="button" class="ezri-btn ezri-btn-primary ezri-confirm"
                          [disabled]="card.state === 'busy'" (click)="confirm(entry.id)">
                    {{ 'assistant.confirm' | translate }}
                  </button>
                  <button type="button" class="ezri-btn ezri-btn-secondary ezri-cancel"
                          [disabled]="card.state === 'busy'" (click)="cancel(entry.id)">
                    {{ 'assistant.cancel' | translate }}
                  </button>
                </div>
                @if (card.errorCode) {
                  <div class="ezri-card-error" role="alert">{{ errorText(card.errorCode) }}</div>
                }
              } @else {
                <div class="ezri-card-result" [class.ezri-card-result-ok]="isDone(card.resultCode)">
                  <mat-icon aria-hidden="true">{{ isDone(card.resultCode) ? 'check_circle' : 'info' }}</mat-icon>
                  <span>{{ resultText(card.resultCode, card.action.tool) }}</span>
                </div>
                @if (card.link) {
                  <button type="button" class="ezri-btn ezri-btn-secondary ezri-open-result" (click)="openLink(card.link)">
                    {{ 'assistant.openResult' | translate }}
                  </button>
                }
              }
            </div>
          }
        </div>
      } @else if (entry.kind === 'receipt_error') {
        <div class="ezri-error ezri-receipt-error" role="alert">
          <mat-icon aria-hidden="true">{{ entry.code === 'plan_limit' ? 'workspace_premium' : 'error_outline' }}</mat-icon>
          <span>{{ receiptErrorText(entry.code) }}</span>
          @if (entry.code === 'plan_limit') {
            <button type="button" class="ezri-btn ezri-btn-secondary ezri-upgrade" (click)="openLink('/pricing')">
              {{ 'assistant.receipt.upgrade' | translate }}
            </button>
          }
        </div>
      } @else {
        <div class="ezri-error" role="alert">
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          <span>{{ errorText(entry.code) }}</span>
        </div>
      }
    }

    @if (store.sending() && store.scanningEntryId() === null) {
      <div class="ezri-row ezri-row-bot">
        <div class="ezri-bubble ezri-bubble-bot ezri-typing">
          <span class="ezri-dot"></span><span class="ezri-dot"></span><span class="ezri-dot"></span>
          <span class="ezri-sr-only">{{ 'assistant.thinking' | translate }}</span>
        </div>
      </div>
    }
  </div>

  <footer class="ezri-footer">
    @if (showMessagesLeft()) {
      <div class="ezri-left">{{ 'assistant.messagesLeft' | translate: { count: store.messagesLeft() } }}</div>
    }
    @if (monthlyLeft(); as left) {
      <div class="ezri-left ezri-monthly-left">{{ 'assistant.freeMessagesLeft' | translate: { count: left } }}</div>
    }
    @if (monthlyLimitReached()) {
      <!-- The month's free messages: a new conversation would not help. -->
      <div class="ezri-error ezri-monthly-limit" role="status">
        <mat-icon aria-hidden="true">workspace_premium</mat-icon>
        <span>{{ 'assistant.errors.limit_monthly' | translate }}</span>
        <button type="button" class="ezri-btn ezri-btn-secondary ezri-upgrade" (click)="openLink('/pricing')">
          {{ 'assistant.receipt.upgrade' | translate }}
        </button>
      </div>
    } @else if (store.limitReached()) {
      <button type="button" class="ezri-btn ezri-btn-primary ezri-new-conversation" (click)="newConversation()">
        <mat-icon aria-hidden="true">add_comment</mat-icon>
        {{ 'assistant.newConversation' | translate }}
      </button>
    } @else {
      @if (store.stagedReceipt(); as staged) {
        <!-- Read, but its message did not go through: the next send carries it. -->
        <div class="ezri-staged" dir="auto">
          <mat-icon aria-hidden="true">{{ staged.attachment.kind === 'pdf' ? 'picture_as_pdf' : 'receipt_long' }}</mat-icon>
          <span class="ezri-file-name">{{ staged.attachment.name }}</span>
          <button type="button" class="ezri-icon-btn ezri-staged-remove" (click)="store.dropStagedReceipt()"
                  [attr.aria-label]="'assistant.receipt.remove' | translate"
                  [attr.title]="'assistant.receipt.remove' | translate">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
      <div class="ezri-composer">
        @if (receipts()) {
          <div class="ezri-attach">
            <button #attachButton type="button" class="ezri-attach-btn" (click)="toggleAttachMenu()"
                    [disabled]="!canAttach()"
                    aria-haspopup="menu" [attr.aria-expanded]="attachMenuOpen()"
                    [attr.aria-label]="'assistant.receipt.attach' | translate"
                    [attr.title]="'assistant.receipt.attach' | translate">
              <mat-icon>attach_file</mat-icon>
            </button>
            @if (attachMenuOpen()) {
              <div class="ezri-attach-menu" role="menu" [attr.aria-label]="'assistant.receipt.attach' | translate">
                @if (isTouch) {
                  <button type="button" role="menuitem" class="ezri-attach-item ezri-take-photo" (click)="takePhoto()">
                    <mat-icon aria-hidden="true">photo_camera</mat-icon>
                    <span>{{ 'assistant.receipt.takePhoto' | translate }}</span>
                  </button>
                }
                <button type="button" role="menuitem" class="ezri-attach-item ezri-choose-file" (click)="chooseFile()">
                  <mat-icon aria-hidden="true">upload_file</mat-icon>
                  <span>{{ 'assistant.receipt.chooseFile' | translate }}</span>
                </button>
                @if (demo()) {
                  <button type="button" role="menuitem" class="ezri-attach-item ezri-try-sample" (click)="trySample()">
                    <mat-icon aria-hidden="true">receipt_long</mat-icon>
                    <span>{{ 'assistant.receipt.trySample' | translate }}</span>
                  </button>
                }
              </div>
            }
            <input #fileInput type="file" class="ezri-file-input" [attr.accept]="receiptAccept"
                   tabindex="-1" aria-hidden="true" (change)="onFileChosen($event)">
            <input #cameraInput type="file" class="ezri-file-input" accept="image/*" capture="environment"
                   tabindex="-1" aria-hidden="true" (change)="onFileChosen($event)">
          </div>
        }
        <textarea #messageInput class="ezri-input" rows="1" dir="auto"
                  [attr.maxlength]="maxLength"
                  [ngModel]="draft()" (ngModelChange)="draft.set($event)"
                  (keydown)="onKeydown($event)"
                  [placeholder]="'assistant.placeholder' | translate"
                  [attr.aria-label]="'assistant.placeholder' | translate"></textarea>
        <button type="button" class="ezri-send" (click)="send()" [disabled]="!canSend()"
                [attr.aria-label]="'assistant.send' | translate"
                [attr.title]="'assistant.send' | translate">
          <mat-icon class="ezri-send-icon">send</mat-icon>
        </button>
      </div>
      @if (draft().length > maxLength - 200) {
        <div class="ezri-count">{{ draft().length }} / {{ maxLength }}</div>
      }
    }
    <p class="ezri-disclaimer">{{ 'assistant.disclaimer' | translate }}</p>
  </footer>
  }
</section>
